// The ONE §2.12 inline danger confirm (UI Layering Sweep Phase 2): a bare
// danger trigger is never allowed, so every destructive row action reveals a
// sentence naming the exact target plus Cancel (secondary) then the danger
// Confirm — danger always last. The component is fully controlled: the caller
// holds `confirmingId` and passes `isConfirming` per row, resets it when the
// mutation settles, and leaves it set on failure so the confirm stays open.
// Not for archive-grade actions (those add the name-match TextInput pattern).

import { Button, Inline, Stack, Text } from "../../ui";

export interface RowConfirmProps {
  // Cancel button label. Default "Cancel".
  cancelLabel?: string;
  // Revealed danger button label, e.g. "Confirm revoke".
  confirmLabel: string;
  // True when this row's confirm is revealed (caller holds confirmingId).
  isConfirming: boolean;
  // True while this row's destructive mutation is in flight; disables both
  // revealed buttons and swaps the confirm label for `pendingLabel`.
  isPending: boolean;
  // Disable the trigger without revealing — e.g. while another row's
  // mutation is in flight (serialized deletes).
  isTriggerDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onStart: () => void;
  // In-flight confirm label, e.g. "Revoking...".
  pendingLabel: string;
  // The §2.12 description sentence naming the exact target, e.g.
  // `Revoke API key ingest-prod (mk_a1b2)? This disables the credential.`
  sentence: string;
  // Collapsed trigger label, e.g. "Revoke" / "Remove" / "Delete".
  triggerLabel: string;
}

export function RowConfirm({
  cancelLabel = "Cancel",
  confirmLabel,
  isConfirming,
  isPending,
  isTriggerDisabled = false,
  onCancel,
  onConfirm,
  onStart,
  pendingLabel,
  sentence,
  triggerLabel,
}: RowConfirmProps) {
  if (!isConfirming) {
    return (
      <Button
        disabled={isTriggerDisabled || isPending}
        size="sm"
        type="button"
        variant="danger"
        onClick={onStart}
      >
        {triggerLabel}
      </Button>
    );
  }

  return (
    <Stack gap={2}>
      <Text variant="muted">{sentence}</Text>
      <Inline>
        <Button
          disabled={isPending}
          size="sm"
          type="button"
          variant="secondary"
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
        <Button
          disabled={isPending}
          size="sm"
          type="button"
          variant="danger"
          onClick={onConfirm}
        >
          {isPending ? pendingLabel : confirmLabel}
        </Button>
      </Inline>
    </Stack>
  );
}
